import { useMatch, useNavigate } from "@solidjs/router";
import { View, Text, IntrinsicNodeProps, ElementNode } from "@lightningtv/solid";
import { Column } from "@lightningtv/solid/primitives";
import styles from "./NavDrawer.styles";
import { createMemo } from "solid-js";
import Icon from "../Icon";
import theme from "theme";

interface NavButtonProps extends IntrinsicNodeProps {
  icon: string;
  iconColor: string;
  children: string;
}

const NavButtonTextStyles = {
  x: 112,
  fontSize: 38,
  lineHeight: 76,
  alpha: 0,
  color: theme.textPrimary,
  $active: {
    alpha: 1
  }
} as const;

function NavButton(props: NavButtonProps) {
  return (
    <View {...props} forwardStates style={styles.NavButton}>
      <View y={-16}>
        <Icon color={props.iconColor} scale={0.5} name={props.icon} />
      </View>
      <Text style={NavButtonTextStyles}>{props.children}</Text>
    </View>
  );
}

export default function NavDrawer(props) {
  let backdrop: ElementNode | undefined;
  const navigate = useNavigate();
  function onFocus(this: ElementNode) {
    backdrop!.states.add("$focus");
    this.children.forEach((c) => c.states!.add("$active"));
    this.children[this.selected || 0].setFocus();
  }

  function onBlur(this: ElementNode) {
    backdrop!.states.remove("$focus");
    this.selected = 0;
    this.children.forEach((c) => c.states!.remove("$active"));
  }

  function handleNavigate(page: string) {
    const isOnPage = useMatch(() => page);
    if (isOnPage()) {
      return props.focusPage();
    }

    navigate(page);
  }

  const selectedButton = createMemo(() => {
    const baseY = 308; // starting Y position for the first button
    const step = 90; // vertical distance between buttons
    if (useMatch(() => "/browse/all")()) return baseY + 0 * step;
    if (useMatch(() => "/browse/movie")()) return baseY + 1 * step;
    if (useMatch(() => "/browse/tv")()) return baseY + 2 * step;
    if (useMatch(() => "/tmdb")()) return baseY + 3 * step;
    // default to first button position
    return baseY;
  });

  return (
    <>
      <View
        id="NavDrawer"
        flexItem={false}
        width={300}
        height={150}
        x={30}
        y={15}
        zIndex={105}
        alpha={props.showWidgets ? 1 : 0}
      >
        <Text y={8} x={80} fontSize={28} color={theme.textSecondary}>
          Built With:
        </Text>
        <View y={10} src="./assets/solidWord.png" width={280} height={52} textureOptions={{}} />

        <View x={0} y={100} src="./assets/tmdb.png" width={80} height={41} textureOptions={{}} />
        <Text x={90} y={104} contain="width" width={160} fontSize={12} color={theme.textSecondary}>
          This product uses the TMDB API but is not endorsed or certified by TMDB.
        </Text>
      </View>
      <Column {...props} onFocus={onFocus} onBlur={onBlur} style={styles.Column} announce={"Main Menu"} scroll="none">
        <NavButton
          onEnter={() => handleNavigate("/browse/all")}
          iconColor={"#fff"}
          announce={["Trending Browse", "button"]}
          icon="trending"
        >
          Trending
        </NavButton>
        <NavButton
          icon="movie"
          iconColor={"#fff"}
          announce={["Movies Browse", "button"]}
          onEnter={() => handleNavigate("/browse/movie")}
        >
          Movies
        </NavButton>
        <NavButton
          icon="tv"
          iconColor={"#fff"}
          announce={["TV Browse", "button"]}
          onEnter={() => handleNavigate("/browse/tv")}
        >
          TV
        </NavButton>

        <NavButton icon="perf" iconColor={"#fff"} announce={["TMDB", "button"]} onEnter={() => handleNavigate("/tmdb")}>
          TMDB
        </NavButton>
      </Column>
      <View skipFocus ref={backdrop} style={styles.Gradient} />
      <View width={4} height={56} color={"#FFF"} x={22} y={selectedButton()} zIndex={100} />
    </>
  );
}
